import { computed, type Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/shared/graphql/router'
import { FilePermission, FileRole } from '@/shared/graphql/router/graphql'

const COPYRIGHT_FILE_QUERY = graphql(`
  query CopyrightFile($id: ID!) {
    file(id: $id) {
      id
      name
      description
      type
      role
      permissions
      asset {
        previewImageUrl
        ... on DocumentAsset {
          totalPages
          documentUrl
        }
        ... on StreamAsset {
          streamUrl
        }
      }
      metadata {
        originalFilename
        size
      }
      settings {
        isPrivate
      }
      copyright {
        id
        status
        registrations {
          certificateUrl
          origin {
            id
            name
            logo {
              black {
                url
              }
            }
          }
          timestamp
        }
      }
      owner {
        ... on User {
          id
          name
          avatar {
            url
          }
        }
        ... on Organization {
          id
          name
          avatar {
            url
          }
        }
      }
      registrant {
        id
        name
      }
      authors(input: { first: 3, filterBy: ACCEPTED }) {
        totalCount
        edges {
          node {
            ... on EntityWithRole {
              entity {
                ... on User {
                  id
                  name
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                }
              }
            }
            ... on EmailInvite {
              email
            }
          }
        }
      }
      coOwners(input: { first: 3, filterBy: ACCEPTED }) {
        totalCount
        edges {
          node {
            ... on EntityWithRole {
              entity {
                ... on User {
                  id
                  name
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                }
              }
            }
            ... on EmailInvite {
              email
            }
          }
        }
      }
      viewers(input: { first: 20 }) {
        totalCount
      }
      analysis {
        summary
        attributes {
          key
          value
        }
        markers {
          color
          shine
          visible
        }
        ... on AudioAnalysis {
          transcript {
            preview {
              text
              timestamp
            }
            url
          }
          tracks {
            summary
            url
            attributes {
              key
              value
            }
            transcript {
              preview {
                text
                timestamp
              }
              url
            }
            stems {
              amplitudeUrl
              breakdown {
                name
                url
                sheetMusic {
                  url
                  preview
                }
              }
            }
          }
        }
        ... on VideoAnalysis {
          frames
          transcript {
            preview {
              text
              timestamp
            }
            url
          }
          tracks {
            summary
            url
            attributes {
              key
              value
            }
            transcript {
              preview {
                text
                timestamp
              }
              url
            }
            stems {
              amplitudeUrl
              breakdown {
                name
                url
                sheetMusic {
                  url
                  preview
                }
              }
            }
          }
        }
      }
      notifications {
        id
        type
        content {
          key
        }
        actions {
          type
          label {
            key
          }
          buttonType
        }
        details {
          ... on InviteNotificationDetails {
            from {
              id
              name
            }
          }
        }
      }
    }
  }
`)

export type CopyrightFile = NonNullable<
  ReturnType<typeof useCopyrightFile>['file']['value']
>

export function useCopyrightFile(id: Ref<string>) {
  const { result, loading, error } = useQuery(
    COPYRIGHT_FILE_QUERY,
    () => ({ id: id.value }),
    { clientId: 'router' }
  )

  const file = computed(() => result.value?.file ?? undefined)

  const isOwner = computed(() => file.value?.role === FileRole.Owner)
  const isCoOwner = computed(() => file.value?.role === FileRole.CoOwner)
  const isAuthor = computed(() => file.value?.role === FileRole.Author)

  const hasPrivilegedRole = computed(() =>
    [FileRole.Owner, FileRole.CoOwner, FileRole.Author].includes(
      file.value?.role as FileRole
    )
  )

  const canDownload = computed(
    () => file.value?.permissions.includes(FilePermission.Download) ?? false
  )

  const isPrivate = computed(() => file.value?.settings.isPrivate ?? false)

  const copyrightId = computed(() => file.value?.copyright.id)

  const primaryRegistration = computed(
    () => file.value?.copyright.registrations[0]
  )

  return {
    file,
    loading,
    error,
    isOwner,
    isCoOwner,
    isAuthor,
    hasPrivilegedRole,
    canDownload,
    isPrivate,
    copyrightId,
    primaryRegistration,
  }
}
